/**
 * Shared telephony-filter utilities used by both `/phone-sim` and the
 * CallCenter "phone simulation" settings drawer.
 *
 * The chain mimics what voice goes through on a real PSTN/mobile call:
 *
 *   bandpass 300–3400 Hz → AGC (DynamicsCompressor) → G.711 µ-law roundtrip
 *   → optional band-limited line-noise mixer
 *
 * Everything here is rate-agnostic — the same nodes work whether the
 * surrounding AudioContext runs at 8 kHz, 16 kHz, or browser-native
 * 48 kHz, because biquad/waveshaper/gain are all defined relative to
 * the context's sample rate.
 */

// ── Types ────────────────────────────────────────────────────────────

export type FilterMode = "off" | "light" | "full" | "custom";

export interface FilterConfig {
  bandpass: boolean;     // 300–3400 Hz telephony bandpass + AGC compressor
  ulaw: boolean;         // G.711 µ-law encode/decode roundtrip (codec artefacts)
  noise: boolean;        // line/handset background noise floor
  noiseSnrDb: number;    // signal-to-noise ratio (dB) — higher = quieter noise
  applyOnInput: boolean; // filter mic → server
  applyOnOutput: boolean;// filter server → speaker
}

// ── Constants ────────────────────────────────────────────────────────

export const HP_HZ = 300;
export const LP_HZ = 3400;
const NOISE_BUFFER_SECONDS = 4;

// ── Profiles ─────────────────────────────────────────────────────────

export function profileToConfig(mode: FilterMode): FilterConfig {
  switch (mode) {
    case "off":
      return {
        bandpass: false,
        ulaw: false,
        noise: false,
        noiseSnrDb: 30,
        applyOnInput: false,
        applyOnOutput: false,
      };
    case "light":
      return {
        bandpass: true,
        ulaw: false,
        noise: false,
        noiseSnrDb: 30,
        applyOnInput: true,
        applyOnOutput: true,
      };
    case "full":
      return {
        bandpass: true,
        ulaw: true,
        noise: true,
        noiseSnrDb: 28,
        applyOnInput: true,
        applyOnOutput: true,
      };
    case "custom":
    default:
      return {
        bandpass: true,
        ulaw: false,
        noise: false,
        noiseSnrDb: 30,
        applyOnInput: true,
        applyOnOutput: true,
      };
  }
}

// ── G.711 µ-law (ITU-T) ──────────────────────────────────────────────

function linear2ulaw(sample: number): number {
  const BIAS = 0x84;
  const CLIP = 32635;
  const sign = sample < 0 ? 0x80 : 0;
  let s = sign ? -sample : sample;
  if (s > CLIP) s = CLIP;
  s = s + BIAS;
  let exponent = 7;
  for (let mask = 0x4000; (s & mask) === 0 && exponent > 0; mask >>= 1) exponent--;
  const mantissa = (s >> (exponent + 3)) & 0x0f;
  return (~(sign | (exponent << 4) | mantissa)) & 0xff;
}

function ulaw2linear(ulawByte: number): number {
  const BIAS = 0x84;
  const u = ~ulawByte;
  const sign = u & 0x80;
  const exponent = (u >> 4) & 0x07;
  const mantissa = u & 0x0f;
  let s = ((mantissa << 3) + BIAS) << exponent;
  s -= BIAS;
  return sign ? -s : s;
}

let _muLawCurveCache: Float32Array | null = null;

/** WaveShaper curve that applies a full G.711 µ-law roundtrip. Cached. */
export function getMuLawCurve(length: number = 65536): Float32Array {
  if (_muLawCurveCache && _muLawCurveCache.length === length) return _muLawCurveCache;
  const curve = new Float32Array(length);
  for (let i = 0; i < length; i++) {
    const x = -1 + (2 * i) / (length - 1);
    const sample = Math.max(-32768, Math.min(32767, Math.round(x * 32767)));
    const round = ulaw2linear(linear2ulaw(sample));
    curve[i] = Math.max(-1, Math.min(1, round / 32768));
  }
  _muLawCurveCache = curve;
  return curve;
}

// ── Noise generation ─────────────────────────────────────────────────

/**
 * Band-limited noise (~200 Hz–3 kHz) — sounds like phone-line hiss
 * rather than the harsh white-noise hash you get from raw Math.random.
 */
export function makeNoiseBuffer(ctx: AudioContext): AudioBuffer {
  const length = Math.floor(ctx.sampleRate * NOISE_BUFFER_SECONDS);
  const buf = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buf.getChannelData(0);

  const lpAlpha = Math.exp(-2 * Math.PI * 3000 / ctx.sampleRate);
  const hpAlpha = Math.exp(-2 * Math.PI * 200 / ctx.sampleRate);
  let lp = 0;
  let hpPrevIn = 0;
  let hpPrevOut = 0;
  for (let i = 0; i < length; i++) {
    const white = (Math.random() * 2 - 1) * 0.5;
    lp = lpAlpha * lp + (1 - lpAlpha) * white;
    const hp = hpAlpha * (hpPrevOut + lp - hpPrevIn);
    hpPrevIn = lp;
    hpPrevOut = hp;
    data[i] = hp;
  }
  return buf;
}

/**
 * Translate an SNR-dB target into a GainNode value for the noise source.
 * Assumes signal nominally peaks near 1.0; the noise buffer is generated
 * at amplitude ≈ 0.5 so the *2 factor compensates.
 */
export function snrDbToGain(snrDb: number): number {
  return Math.pow(10, -snrDb / 20) * 2;
}

// ── Chain builders ───────────────────────────────────────────────────

export interface BuiltInputChain {
  /** Where the mic source should connect. */
  entry: AudioNode;
  /** Where the ScriptProcessor / next stage should read from. */
  exit: AudioNode;
  /** Live-tunable gain node for the noise mixer. Always present (gain=0 when disabled). */
  noiseGain: GainNode;
  /** Buffer source feeding the noise mixer — call `.stop()` on teardown. */
  noiseSource: AudioBufferSourceNode | null;
}

export interface BuiltOutputChain {
  /** Where each playback BufferSource should connect. */
  entry: AudioNode;
  /** Live-tunable gain node for the noise mixer. */
  noiseGain: GainNode;
  /** Buffer source feeding the noise mixer — call `.stop()` on teardown. */
  noiseSource: AudioBufferSourceNode | null;
}

/** Build the mic-side filter chain. Connect the mic source to `entry`, the next stage reads `exit`. */
export function buildInputFilterChain(
  ctx: AudioContext,
  cfg: FilterConfig | null
): BuiltInputChain {
  const entry = ctx.createGain();
  let tail: AudioNode = entry;

  if (cfg && cfg.applyOnInput && cfg.bandpass) {
    const hp = ctx.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = HP_HZ;
    hp.Q.value = 0.707;
    tail.connect(hp);
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = LP_HZ;
    lp.Q.value = 0.707;
    hp.connect(lp);
    tail = lp;

    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -28;
    comp.knee.value = 18;
    comp.ratio.value = 6;
    comp.attack.value = 0.005;
    comp.release.value = 0.12;
    tail.connect(comp);
    const makeup = ctx.createGain();
    makeup.gain.value = 1.6;
    comp.connect(makeup);
    tail = makeup;
  }

  if (cfg && cfg.applyOnInput && cfg.ulaw) {
    const ws = ctx.createWaveShaper();
    ws.curve = getMuLawCurve();
    ws.oversample = "none";
    tail.connect(ws);
    tail = ws;
  }

  const mixer = ctx.createGain();
  mixer.gain.value = 1;
  tail.connect(mixer);

  // Always create the noise node so its gain can be ramped live —
  // the noise toggle / SNR slider work mid-call without rebuilding
  // the graph.
  const noiseGain = ctx.createGain();
  let noiseSource: AudioBufferSourceNode | null = null;
  if (cfg && cfg.applyOnInput) {
    noiseSource = ctx.createBufferSource();
    noiseSource.buffer = makeNoiseBuffer(ctx);
    noiseSource.loop = true;
    noiseGain.gain.value = cfg.noise ? snrDbToGain(cfg.noiseSnrDb) : 0;
    noiseSource.connect(noiseGain).connect(mixer);
    noiseSource.start();
  } else {
    noiseGain.gain.value = 0;
  }

  return { entry, exit: mixer, noiseGain, noiseSource };
}

/**
 * Build the playback-side filter chain. Each incoming chunk's
 * BufferSource should connect to `entry`; the chain exits at the
 * context's destination.
 */
export function buildOutputFilterChain(
  ctx: AudioContext,
  cfg: FilterConfig | null,
  destination?: AudioNode
): BuiltOutputChain {
  const dest = destination ?? ctx.destination;
  const entry = ctx.createGain();
  let tail: AudioNode = entry;

  if (cfg && cfg.applyOnOutput && cfg.bandpass) {
    const hp = ctx.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = HP_HZ;
    hp.Q.value = 0.707;
    tail.connect(hp);
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = LP_HZ;
    lp.Q.value = 0.707;
    hp.connect(lp);
    tail = lp;
  }

  if (cfg && cfg.applyOnOutput && cfg.ulaw) {
    const ws = ctx.createWaveShaper();
    ws.curve = getMuLawCurve();
    ws.oversample = "none";
    tail.connect(ws);
    tail = ws;
  }

  tail.connect(dest);

  const noiseGain = ctx.createGain();
  let noiseSource: AudioBufferSourceNode | null = null;
  if (cfg && cfg.applyOnOutput) {
    noiseSource = ctx.createBufferSource();
    noiseSource.buffer = makeNoiseBuffer(ctx);
    noiseSource.loop = true;
    noiseGain.gain.value = cfg.noise ? snrDbToGain(cfg.noiseSnrDb) : 0;
    noiseSource.connect(noiseGain).connect(dest);
    noiseSource.start();
  } else {
    noiseGain.gain.value = 0;
  }

  return { entry, noiseGain, noiseSource };
}

/**
 * Smoothly retarget a noise gain to match a (possibly updated) config.
 * Call from a live useEffect that watches `filterConfig`. `side` says
 * which `applyOn{Input,Output}` flag to honour.
 */
export function applyLiveNoise(
  ctx: AudioContext,
  gain: GainNode | null,
  cfg: FilterConfig | null,
  side: "input" | "output"
): void {
  if (!gain) return;
  const sideEnabled =
    side === "input" ? cfg?.applyOnInput : cfg?.applyOnOutput;
  const target =
    cfg?.noise && sideEnabled ? snrDbToGain(cfg.noiseSnrDb) : 0;
  try {
    gain.gain.cancelScheduledValues(ctx.currentTime);
    gain.gain.linearRampToValueAtTime(target, ctx.currentTime + 0.05);
  } catch {
    gain.gain.value = target;
  }
}
